import { productImageAlt } from "@/lib/imageAlt";
import { useState, useEffect, useRef, useMemo } from "react";
import { Search, X, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { searchProducts, SearchResult, formatPrice, getShopifyImageUrl } from "@/lib/shopify";
import { listingPrice } from "@/lib/variants";
import { trackSearch } from "@/lib/analytics";
import { cleanProductTitle, uniqueDisplayNames, dedupeProducts } from "@/lib/productTitle";
import { Button } from "@/components/ui/button";

const PREVIEW_COUNT = 6;

export const SearchOverlay = ({ onClose }: { onClose: () => void }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const navigate = useNavigate();

  // A modal: focus moves in, Tab stays in, Escape closes, and focus goes back
  // to the button that opened it (it used to be left on <body>).
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Prevented, so the panels underneath (chat, accessibility) leave this one alone.
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const stops = Array.from(overlayRef.current?.querySelectorAll<HTMLElement>("a[href], button:not([disabled]), input") ?? []);
      if (!stops.length) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      const inside = overlayRef.current?.contains(document.activeElement);
      if (e.shiftKey ? !inside || document.activeElement === first : !inside || document.activeElement === last) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };
    // Capture: this is the top layer, so it hears the key before those panels do.
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    setSearchFailed(false);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchProducts(query.trim(), 12);
        setResults(data);
        // GA4 records the term and result count. The search_analytics insert that
        // followed was rejected by row-level security on every search (HTTP 401)
        // and loaded the Supabase client for nothing.
        trackSearch(query.trim(), data.length);
      } catch {
        setResults([]);
        setSearchFailed(true);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Same naming pipeline as the grids: dedupe feed duplicates, then guarantee
  // that two visible rows never share a display name.
  const { preview, names } = useMemo(() => {
    const wrapped = results.map((r) => ({ node: r }));
    const shown = dedupeProducts(wrapped).slice(0, PREVIEW_COUNT);
    return { preview: shown, names: uniqueDisplayNames(shown) };
  }, [results]);

  const goToResults = () => {
    const q = query.trim();
    if (q.length < 2) return;
    onClose();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div ref={overlayRef} role="dialog" aria-modal="true" aria-label="Search products" className="fixed inset-0 z-[60] bg-background overflow-y-auto overscroll-contain animate-fade-in">
      <div className="container max-w-2xl pt-6 md:pt-20 pb-24">
        {/* Search input */}
        <div className="relative mb-6">
          <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") goToResults(); }}
            placeholder="Search products..."
            aria-label="Search products"
            className="w-full pl-8 pr-10 py-3 bg-transparent border-b-2 border-foreground text-lg font-medium placeholder:text-muted-foreground focus:outline-none"
          />
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close search" className="absolute -right-3 top-1/2 -translate-y-1/2 h-11 w-11 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Results */}
        {loading && <p className="text-sm text-muted-foreground">Searching...</p>}
        {!loading && query.trim().length >= 2 && preview.length === 0 && (
          <div className="mb-6">
            {searchFailed ? (
              <p className="text-sm text-foreground font-medium">Search is temporarily unavailable. Please try again.</p>
            ) : (
              <>
                <p className="text-sm text-foreground font-medium">No matches for “{query.trim()}”.</p>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                  Try a broader term, or browse by room —{" "}
                  <Link to="/categories" onClick={onClose} className="text-primary hover:underline">
                    see all categories
                  </Link>
                  .
                </p>
              </>
            )}
          </div>
        )}
        <div className="space-y-1 max-h-[52vh] overflow-y-auto">
          {preview.map((p) => {
            const node = p.node;
            const img = node.images?.edges?.[0]?.node;
            const { price, from: priceIsFrom } = listingPrice(node);
            return (
              <Link
                key={node.id}
                to={`/product/${node.handle}`}
                onClick={onClose}
                className="flex items-center gap-4 p-3 hover:bg-muted/50 transition-colors rounded-sm"
              >
                {img ? (
                  <img src={getShopifyImageUrl(img.url, 160)} alt={productImageAlt(node.title, { productType: node.productType })} loading="lazy" decoding="async" className="w-14 h-14 object-contain bg-muted/30 flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 bg-muted/30 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground line-clamp-2 leading-snug">
                    {names[node.handle] || cleanProductTitle(node.title, node.productType)}
                  </p>
                  {price && <p className="text-xs text-muted-foreground mt-0.5">{priceIsFrom && "From "}{formatPrice(price.amount, price.currencyCode)}</p>}
                </div>
              </Link>
            );
          })}
        </div>

        {/* Persistent link to the full results page */}
        {query.trim().length >= 2 && (
          <Button variant="ghost"
            onClick={goToResults}
            className="mt-3 w-full min-h-11 h-auto flex items-center justify-between gap-3 border-t border-border py-4 px-0 text-left whitespace-normal group"
          >
            <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
              View all results for “{query.trim()}”
            </span>
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
          </Button>
        )}

        {/* Quick links — always available, including on an empty result set */}
        {(query.trim().length < 2 || preview.length === 0) && (
          <div className="mt-6">
            <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3 font-medium">Popular Searches</p>
            <div className="flex flex-wrap gap-2">
              {["Sofa", "Dining Table", "Rug", "Lamp", "Outdoor"].map((term) => (
                <Button variant="outline"
                  key={term}
                  onClick={() => setQuery(term)}
                  className="min-h-11 px-4 text-sm text-muted-foreground hover:text-foreground hover:border-foreground"
                >
                  {term}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
